// src/pages/admin/DashboardPage.js
import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Chip,
  Button,
  LinearProgress,
  IconButton,
  useTheme,
  alpha
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  CleaningServices as CleaningIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Star as StarIcon,
  Notifications as NotificationsIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  MoreVert as MoreIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  CheckCircle as CompletedIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

// Mock data for the dashboard
const dashboardData = {
  stats: {
    totalRevenue: 12540,
    revenueChange: 12.5,
    totalBookings: 156,
    bookingsChange: 8.2,
    activeCleaners: 24,
    cleanersChange: 5.7,
    customerSatisfaction: 4.8,
    satisfactionChange: 2.1,
  },
  recentBookings: [
    {
      id: 1,
      customer: "Sarah Johnson",
      cleaner: "Maria Garcia",
      service: "Deep Cleaning",
      date: "2024-03-23",
      time: "10:00 AM",
      status: "completed",
      amount: 189,
      rating: 5,
    },
    {
      id: 2,
      customer: "Mike Brown",
      cleaner: "James Wilson",
      service: "Standard Cleaning",
      date: "2024-03-23",
      time: "2:00 PM",
      status: "in-progress",
      amount: 89,
      rating: null,
    },
    {
      id: 3,
      customer: "Emily Davis",
      cleaner: "Carlos Rodriguez",
      service: "Move-In Cleaning",
      date: "2024-03-22",
      time: "9:00 AM",
      status: "completed",
      amount: 249,
      rating: 4,
    },
    {
      id: 4,
      customer: "David Wilson",
      cleaner: "Lisa Chen",
      service: "Office Cleaning",
      date: "2024-03-24",
      time: "11:00 AM",
      status: "scheduled",
      amount: 149,
      rating: null,
    },
  ],
  performanceMetrics: {
    completionRate: 92,
    onTimeRate: 88,
    customerRetention: 78,
    cleanerRetention: 85,
  },
  upcomingSchedule: [
    {
      id: 1,
      customer: "Jennifer Lopez",
      service: "Standard Cleaning",
      time: "Today, 9:00 AM",
      address: "123 Park Ave, New York",
      status: "confirmed",
    },
    {
      id: 2,
      customer: "Robert Smith",
      service: "Deep Cleaning",
      time: "Today, 1:00 PM",
      address: "456 Broadway, New York",
      status: "confirmed",
    },
    {
      id: 3,
      customer: "Amanda Clark",
      service: "Move-Out Cleaning",
      time: "Tomorrow, 10:00 AM",
      address: "789 5th Ave, New York",
      status: "pending",
    },
  ],
  topCleaners: [
    {
      id: 1,
      name: "Maria Garcia",
      jobs: 156,
      rating: 4.9,
      earnings: 12540,
      status: "available",
    },
    {
      id: 2,
      name: "James Wilson",
      jobs: 132,
      rating: 4.8,
      earnings: 9840,
      status: "working",
    },
    {
      id: 3,
      name: "Carlos Rodriguez",
      jobs: 98,
      rating: 4.7,
      earnings: 7560,
      status: "available",
    },
  ],
};

const StatCard = ({ title, value, change, icon, color }) => {
  const theme = useTheme();
  const isPositive = change >= 0;

  return (
    <Card 
      sx={{ 
        height: '100%',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" color={color} gutterBottom>
              {typeof value === 'number' && value >= 1000 
                ? `$${(value / 1000).toFixed(1)}k` 
                : value}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color }}>
            {icon}
          </Avatar>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isPositive ? (
            <TrendingUpIcon sx={{ color: 'success.main', fontSize: 20 }} />
          ) : (
            <TrendingDownIcon sx={{ color: 'error.main', fontSize: 20 }} />
          )}
          <Typography 
            variant="body2" 
            color={isPositive ? 'success.main' : 'error.main'}
            fontWeight="600"
          >
            {isPositive ? '+' : ''}{change}%
          </Typography>
          <Typography variant="body2" color="text.secondary">
            from last month
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const PerformanceMetric = ({ label, value, color }) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" fontWeight="600">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight="600" color={color}>
          {value}%
        </Typography>
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={value} 
        sx={{ 
          height: 8, 
          borderRadius: 4,
          backgroundColor: alpha(color, 0.2),
          '& .MuiLinearProgress-bar': {
            backgroundColor: color,
            borderRadius: 4,
          }
        }}
      />
    </Box>
  );
};

const StatusChip = ({ status }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'completed':
        return { color: 'success', label: 'Completed', icon: <CompletedIcon /> };
      case 'in-progress':
        return { color: 'warning', label: 'In Progress', icon: <PendingIcon /> };
      case 'scheduled':
        return { color: 'info', label: 'Scheduled', icon: <CalendarIcon /> };
      case 'confirmed':
        return { color: 'primary', label: 'Confirmed', icon: <CalendarIcon /> };
      case 'pending':
        return { color: 'warning', label: 'Pending', icon: <PendingIcon /> };
      case 'available':
        return { color: 'success', label: 'Available', icon: <CompletedIcon /> };
      case 'working':
        return { color: 'warning', label: 'Working', icon: <PendingIcon /> };
      default:
        return { color: 'default', label: status, icon: null };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      icon={config.icon}
      variant="outlined"
    />
  );
};

export default function DashboardPage() {
  const theme = useTheme();
  const [data, setData] = useState(dashboardData);
  const [loading, setLoading] = useState(false);

  const refreshData = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setData(dashboardData);
      setLoading(false);
    }, 1000);
  };

  const stats = [
    {
      title: "Total Revenue",
      value: data.stats.totalRevenue,
      change: data.stats.revenueChange,
      icon: <MoneyIcon />,
      color: theme.palette.success.main,
    },
    {
      title: "Total Bookings",
      value: data.stats.totalBookings,
      change: data.stats.bookingsChange,
      icon: <CalendarIcon />,
      color: theme.palette.primary.main,
    },
    {
      title: "Active Cleaners",
      value: data.stats.activeCleaners,
      change: data.stats.cleanersChange,
      icon: <PeopleIcon />,
      color: theme.palette.info.main,
    },
    {
      title: "Customer Satisfaction",
      value: data.stats.customerSatisfaction,
      change: data.stats.satisfactionChange,
      icon: <StarIcon />,
      color: theme.palette.warning.main,
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Dashboard Overview
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={refreshData}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
            >
              Export
            </Button>
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Welcome back! Here's what's happening with your cleaning business today.
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Bookings */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="600">
                  Recent Bookings
                </Typography>
                <Button size="small" startIcon={<AddIcon />}>
                  New Booking
                </Button>
              </Box>
              
              <List disablePadding>
                {data.recentBookings.map((booking, index) => (
                  <ListItem
                    key={booking.id}
                    divider={index < data.recentBookings.length - 1}
                    sx={{ py: 2 }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                        <CleaningIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="600">
                            {booking.service}
                          </Typography>
                          <Typography variant="body1" fontWeight="600" color="primary">
                            ${booking.amount}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {booking.customer} • {booking.cleaner}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarIcon fontSize="small" color="action" />
                              <Typography variant="body2">
                                {booking.date} at {booking.time}
                              </Typography>
                            </Box>
                            <StatusChip status={booking.status} />
                            {booking.rating && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <StarIcon sx={{ color: theme.palette.warning.main, fontSize: 18 }} />
                                <Typography variant="body2">{booking.rating}/5</Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    <IconButton size="small">
                      <MoreIcon />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Performance Metrics */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Performance Metrics
              </Typography>
              <PerformanceMetric 
                label="Job Completion Rate" 
                value={data.performanceMetrics.completionRate} 
                color={theme.palette.success.main}
              />
              <PerformanceMetric 
                label="On-Time Rate" 
                value={data.performanceMetrics.onTimeRate} 
                color={theme.palette.info.main}
              />
              <PerformanceMetric 
                label="Customer Retention" 
                value={data.performanceMetrics.customerRetention} 
                color={theme.palette.primary.main}
              />
              <PerformanceMetric 
                label="Cleaner Retention" 
                value={data.performanceMetrics.cleanerRetention} 
                color={theme.palette.warning.main}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          {/* Upcoming Schedule */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Today's Schedule
              </Typography>
              <List disablePadding>
                {data.upcomingSchedule.map((schedule, index) => (
                  <ListItem
                    key={schedule.id}
                    divider={index < data.upcomingSchedule.length - 1}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, width: 32, height: 32 }}>
                        <ScheduleIcon fontSize="small" />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight="600">
                          {schedule.customer}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {schedule.service}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <LocationIcon fontSize="small" color="action" />
                            <Typography variant="caption">
                              {schedule.address}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                      <StatusChip status={schedule.status} />
                      <Typography variant="caption" color="text.secondary">
                        {schedule.time}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
              <Button fullWidth variant="outlined" sx={{ mt: 2 }}>
                View Full Schedule
              </Button>
            </CardContent>
          </Card>

          {/* Top Cleaners */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Top Performers
              </Typography>
              <List disablePadding>
                {data.topCleaners.map((cleaner, index) => (
                  <ListItem
                    key={cleaner.id}
                    divider={index < data.topCleaners.length - 1}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                        {cleaner.name.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight="600">
                          {cleaner.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CleaningIcon fontSize="small" color="action" />
                            <Typography variant="caption">{cleaner.jobs} jobs</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                            <Typography variant="caption">{cleaner.rating}</Typography>
                          </Box>
                        </Box>
                      }
                    />
                    <StatusChip status={cleaner.status} />
                  </ListItem>
                ))}
              </List>
              <Button fullWidth variant="outlined" sx={{ mt: 2 }}>
                View All Cleaners
              </Button>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="contained" fullWidth startIcon={<AddIcon />}>
                  Schedule Cleaning
                </Button>
                <Button variant="outlined" fullWidth startIcon={<PeopleIcon />}>
                  Manage Cleaners
                </Button>
                <Button variant="outlined" fullWidth startIcon={<MoneyIcon />}>
                  Process Payouts
                </Button>
                <Button variant="outlined" fullWidth startIcon={<NotificationsIcon />}>
                  Send Notification
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}